import { ref, type Ref } from 'vue'
import * as THREE from 'three'
import type { SceneManager } from '@/three/SceneManager'
import { getThreeObject } from '@/three/ObjectManager'
import { useSceneStore } from '@/stores/scene'

type SceneStore = ReturnType<typeof useSceneStore>

export interface RotateToolDeps {
  getSm: () => SceneManager | undefined
  sceneStore: SceneStore
  selectedIds: Ref<string[]>
  onBeforeRotate: () => void
}

export function createRotateTool(deps: RotateToolDeps) {
  const transformMode = ref<'move' | 'rotate'>('move')
  const rotateDragging = ref(false)
  const rotateDisplayDeg = ref(0)

  const rotateState = {
    active: false,
    centerX: 0,
    centerY: 0,
    startAngle: 0,
    started: false,
    startRotations: new Map<string, number>(),
  }

  const rotateRaycaster = new THREE.Raycaster()

  function normalizeDeg(v: number) {
    return ((v % 360) + 360) % 360
  }

  function getSelectionCenterScreen() {
    const sm = deps.getSm()
    if (!sm || !deps.selectedIds.value.length) return null
    const box = new THREE.Box3()
    const union = new THREE.Box3()
    const center = new THREE.Vector3()
    let has = false
    for (const sid of deps.selectedIds.value) {
      const obj3d = getThreeObject(sid)
      if (!obj3d) continue
      box.setFromObject(obj3d)
      if (!has) {
        union.copy(box)
        has = true
      } else {
        union.union(box)
      }
    }
    if (!has) return null
    union.getCenter(center)
    center.project(sm.camera)
    const rect = sm.renderer.domElement.getBoundingClientRect()
    return {
      x: ((center.x + 1) / 2) * rect.width + rect.left,
      y: ((-center.y + 1) / 2) * rect.height + rect.top,
    }
  }

  function hitSelectedObject(e: MouseEvent) {
    const sm = deps.getSm()
    if (!sm || !deps.selectedIds.value.length) return false
    const rect = sm.renderer.domElement.getBoundingClientRect()
    const ndc = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1
    )
    rotateRaycaster.setFromCamera(ndc, sm.camera)
    const hits = rotateRaycaster.intersectObjects(sm.scene.children, true)
    return hits.some((h) => {
      const oid = h.object.userData.objectId as string | undefined
      return !!oid && deps.selectedIds.value.includes(oid)
    })
  }

  function onRotateMouseDown(e: MouseEvent) {
    const sm = deps.getSm()
    if (!sm || transformMode.value !== 'rotate') return
    if (e.button !== 0) return
    if (!deps.selectedIds.value.length) return
    if (!hitSelectedObject(e)) return
    const center = getSelectionCenterScreen()
    if (!center) return
    e.preventDefault()
    e.stopPropagation()
    rotateState.active = true
    rotateState.started = false
    rotateState.centerX = center.x
    rotateState.centerY = center.y
    rotateState.startAngle = Math.atan2(e.clientY - center.y, e.clientX - center.x)
    rotateState.startRotations.clear()
    rotateDisplayDeg.value = 0
    rotateDragging.value = true
    deps.selectedIds.value.forEach((sid) => {
      const obj = deps.sceneStore.activeObjects().find((o) => o.id === sid)
      rotateState.startRotations.set(sid, obj?.rotationY ?? 0)
    })
    sm.controls.enabled = false
  }

  function onRotateMouseMove(e: MouseEvent) {
    if (!rotateState.active || transformMode.value !== 'rotate') return
    const current = Math.atan2(e.clientY - rotateState.centerY, e.clientX - rotateState.centerX)
    const deltaDeg = (current - rotateState.startAngle) * 180 / Math.PI
    if (!rotateState.started) {
      deps.onBeforeRotate()
      rotateState.started = true
    }
    deps.selectedIds.value.forEach((sid) => {
      const start = rotateState.startRotations.get(sid) ?? 0
      const raw = start + deltaDeg
      const next = e.shiftKey ? Math.round(raw / 15) * 15 : raw
      deps.sceneStore.updateObject(sid, { rotationY: normalizeDeg(next) })
      if (sid === deps.selectedIds.value[0]) {
        rotateDisplayDeg.value = Math.round(normalizeDeg(next))
      }
    })
  }

  function stopRotateInteraction() {
    const sm = deps.getSm()
    if (!sm) return
    rotateState.active = false
    rotateState.started = false
    rotateState.startRotations.clear()
    rotateDragging.value = false
    sm.controls.enabled = true
    sm.renderer.domElement.style.cursor = ''
  }

  function onRotateMouseUp() {
    if (!rotateState.active) return
    stopRotateInteraction()
  }

  return {
    transformMode,
    rotateDragging,
    rotateDisplayDeg,
    rotateState,
    onRotateMouseDown,
    onRotateMouseMove,
    onRotateMouseUp,
    stopRotateInteraction,
  }
}
